import { format, isValid, isWithinInterval } from "date-fns";
import { nb } from "date-fns/locale";
import type { PeriodType } from "./types";

export type ResolvedPeriod = {
  start: Date;
  end: Date;
  isWithin: boolean;
  // true når minst én av grensene har eksplisitt klokkeslett (DD.MM HH:mm).
  // Brukes til å avgjøre om perioden skal vises med klokkeslett.
  hasTime: boolean;
};

/**
 * Parser en periode-grense "YYYY-MM-DD" eller "YYYY-MM-DD HH:mm" (året
 * ignoreres). Returnerer måned/dag/time/minutt + om klokkeslett var oppgitt.
 */
function parseBoundary(
  value: string,
): { M: number; D: number; h: number; min: number; hasTime: boolean } | null {
  const [datePart, timePart] = value.split(/[ T]/);
  const [, M, D] = datePart.split("-").map(Number);
  if (!M || !D) return null;
  const hasTime = /^\d{1,2}:\d{2}/.test(timePart ?? "");
  const [h, min] = hasTime ? timePart.split(":").map(Number) : [0, 0];
  return { M, D, h: h || 0, min: min || 0, hasTime };
}

/**
 * Bestemmer aktiv eller nærmeste fremtidige forekomst av periodene basert
 * på dag og måned. Året i input ignoreres.
 *
 * Regler:
 * - Hvis startdate (mm-dd) > deadline (mm-dd), krysser perioden årsskiftet
 *   og deadline-året settes til neste år.
 * - Hvis kandidaten allerede er passert i år, rulles begge datoer frem ett år.
 * - Active = today ligger innenfor [start, end] for én eller flere perioder.
 * - Ellers velges perioden med tidligst start > today.
 *
 * Returnerer null hvis periods er tom eller alle datoer er ugyldige.
 */
export function getActiveOrNextPeriod(
  periods: PeriodType[] | undefined,
  today: Date,
): ResolvedPeriod | null {
  if (!periods?.length) return null;

  const todayY = today.getFullYear();
  const candidates: { start: Date; end: Date; hasTime: boolean }[] = [];

  for (const p of periods) {
    // Hopp over ufullstendige perioder (kun start- eller kun sluttdato satt).
    // Admin tillater å lagre én dato om gangen, så feltene kan mangle.
    if (!p.startdate || !p.deadline) continue;
    const s = parseBoundary(p.startdate);
    const e = parseBoundary(p.deadline);
    if (!s || !e) continue;

    const hasTime = s.hasTime || e.hasTime;
    // Uten eksplisitt klokkeslett på slutt-grensa beholder vi gammel
    // semantikk: hele dagen ut (23:59:59.999). Med klokkeslett brukes det.
    const buildStart = (y: number) => new Date(y, s.M - 1, s.D, s.h, s.min, 0, 0);
    const buildEnd = (y: number) =>
      e.hasTime
        ? new Date(y, e.M - 1, e.D, e.h, e.min, 0, 0)
        : new Date(y, e.M - 1, e.D, 23, 59, 59, 999);

    let start = buildStart(todayY);
    let end = buildEnd(todayY);

    // Perioden krysser årsskiftet (start dag/måned etter slutt dag/måned).
    if (s.M > e.M || (s.M === e.M && s.D > e.D)) {
      end = buildEnd(todayY + 1);
    }

    // Allerede passert i år → rull begge frem ett år.
    if (today.getTime() > end.getTime()) {
      start = buildStart(start.getFullYear() + 1);
      end = buildEnd(end.getFullYear() + 1);
    }

    if (!isValid(start) || !isValid(end)) continue;
    candidates.push({ start, end, hasTime });
  }

  if (!candidates.length) return null;

  const active = candidates.find((c) =>
    isWithinInterval(today, { start: c.start, end: c.end }),
  );
  if (active) return { ...active, isWithin: true };

  const future = candidates
    .filter((c) => c.start.getTime() > today.getTime())
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  return future[0] ? { ...future[0], isWithin: false } : null;
}

/**
 * Erstatter {{fra}} og {{til}} i template med formaterte datoer (norsk locale).
 * Begge får årstall — det utledede året fra getActiveOrNextPeriod, ikke året fra API-input.
 *   {{fra}} → "1. september 2026"
 *   {{til}} → "30. september 2026"
 *
 * Returnerer "" for tom/undefined template. Returnerer template uendret hvis
 * period mangler eller har ugyldige datoer (fail-safe mot "Invalid Date" i UI).
 */
export function formatPeriodText(
  template: string | undefined,
  period: Pick<ResolvedPeriod, "start" | "end" | "hasTime"> | null,
): string {
  if (!template) return "";
  if (!period || !isValid(period.start) || !isValid(period.end)) return template;

  // Vis klokkeslett kun når perioden faktisk har det (nye perioder med
  // DD.MM HH:mm). Eldre dato-perioder vises som før, uten "kl. ...".
  const fmt = period.hasTime ? "d. MMMM yyyy 'kl.' HH:mm" : "d. MMMM yyyy";
  const fra = format(period.start, fmt, { locale: nb });
  const til = format(period.end, fmt, { locale: nb });
  return template.replaceAll("{{fra}}", fra).replaceAll("{{til}}", til);
}
